import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import presentationService, { PresentationSection } from '../../services/presentation.service';
import { Save, Loader, Plus, Trash2, Edit2, X } from 'lucide-react';

// --- Generic Components ---

const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
);

const SectionContainer: React.FC<{
    title: string;
    onSave: () => void;
    isSaving: boolean;
    children: React.ReactNode;
}> = ({ title, onSave, isSaving, children }) => (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-8 border border-gray-200">
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
            <h3 className="text-xl font-bold text-gray-800 capitalize">{title}</h3>
            <button
                onClick={onSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-blue-600 border border-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 font-medium"
            >
                {isSaving ? <Loader className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
                Enregistrer
            </button>
        </div>
        {children}
    </div>
);

const InputField: React.FC<{
    label: string;
    value: string;
    onChange: (val: string) => void;
    multiline?: boolean;
}> = ({ label, value, onChange, multiline }) => (
    <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        {multiline ? (
            <textarea
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
        ) : (
            <input
                type="text"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
        )}
    </div>
);

// --- Specific Editors ---

const HeroEditor = ({ content, onChange }: any) => (
    <>
        <InputField label="Badge (ex: Notre Organisation)" value={content.badge} onChange={(v) => onChange({ ...content, badge: v })} />
        <InputField label="Titre Principal" value={content.title} onChange={(v) => onChange({ ...content, title: v })} />
        <InputField label="Sous-titre" value={content.subtitle} onChange={(v) => onChange({ ...content, subtitle: v })} />
    </>
);

const IntroEditor = ({ content, onChange }: any) => {
    const updateParagraph = (idx: number, val: string) => {
        const newParagraphs = [...(content.paragraphs || [])];
        newParagraphs[idx] = val;
        onChange({ ...content, paragraphs: newParagraphs });
    };

    return (
        <>
            <InputField label="Titre" value={content.title} onChange={(v) => onChange({ ...content, title: v })} />
            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Paragraphes</label>
                {content.paragraphs?.map((p: string, idx: number) => (
                    <div key={idx} className="mb-3">
                        <textarea
                            value={p}
                            onChange={(e) => updateParagraph(idx, e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                    </div>
                ))}
            </div>
        </>
    );
};

const ListEditor = ({ content, onChange, itemLabel = "Élément" }: any) => {
    const addItem = () => {
        onChange({ ...content, items: [...(content.items || []), ""] });
    };

    const removeItem = (idx: number) => {
        const newItems = [...(content.items || [])];
        newItems.splice(idx, 1);
        onChange({ ...content, items: newItems });
    };

    const updateItem = (idx: number, val: string) => {
        const newItems = [...(content.items || [])];
        newItems[idx] = val;
        onChange({ ...content, items: newItems });
    };

    return (
        <>
            <InputField label="Titre de la section" value={content.title} onChange={(v) => onChange({ ...content, title: v })} />
            <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Liste des {itemLabel}s</label>
                {content.items?.map((item: string, idx: number) => (
                    <div key={idx} className="flex gap-2 mb-2 items-start">
                        <textarea
                            value={item}
                            onChange={(e) => updateItem(idx, e.target.value)}
                            rows={2}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                        />
                        <button
                            onClick={() => removeItem(idx)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-md"
                            title="Supprimer"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                ))}
                <button
                    onClick={addItem}
                    className="mt-2 flex items-center gap-1 text-sm text-primary font-medium hover:underline"
                >
                    <Plus className="w-4 h-4" /> Ajouter un élément
                </button>
            </div>
        </>
    );
};

const MemberEditor = ({ member, onChange, onDelete, title }: any) => (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-3 relative group">
        <div className="grid grid-cols-2 gap-4">
            <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Nom</label>
                <input
                    type="text"
                    value={member.name}
                    onChange={(e) => onChange({ ...member, name: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
            </div>
            <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Rôle</label>
                <input
                    type="text"
                    value={member.role}
                    onChange={(e) => onChange({ ...member, role: e.target.value })}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
            </div>
        </div>
        {onDelete && (
            <button
                onClick={onDelete}
                className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
            >
                <X className="w-4 h-4" />
            </button>
        )}
    </div>
);

const BureauEditor = ({ content, onChange }: any) => {
    // Helpers for array updates
    const updateArrayItem = (key: string, idx: number, val: any) => {
        const arr = [...(content[key] || [])];
        arr[idx] = val;
        onChange({ ...content, [key]: arr });
    };

    const removeArrayItem = (key: string, idx: number) => {
        const arr = [...(content[key] || [])];
        arr.splice(idx, 1);
        onChange({ ...content, [key]: arr });
    };

    const addArrayItem = (key: string) => {
        onChange({ ...content, [key]: [...(content[key] || []), { name: '', role: '' }] });
    };

    return (
        <>
            <InputField label="Titre" value={content.title} onChange={(v) => onChange({ ...content, title: v })} />
            <InputField label="Sous-titre" value={content.subtitle} onChange={(v) => onChange({ ...content, subtitle: v })} />

            <div className="mt-6">
                <h4 className="font-semibold text-gray-800 mb-3">La Présidente</h4>
                <MemberEditor
                    member={content.president || {}}
                    onChange={(val: any) => onChange({ ...content, president: val })}
                />
            </div>

            <div className="mt-6">
                <h4 className="font-semibold text-gray-800 mb-3">Vice-Présidents</h4>
                {content.vicePresidents?.map((vp: any, idx: number) => (
                    <MemberEditor
                        key={idx}
                        member={vp}
                        onChange={(val: any) => updateArrayItem('vicePresidents', idx, val)}
                        onDelete={() => removeArrayItem('vicePresidents', idx)}
                    />
                ))}
                <button onClick={() => addArrayItem('vicePresidents')} className="text-sm text-primary flex items-center gap-1 mt-2">
                    <Plus className="w-4 h-4" /> Ajouter un Vice-Président
                </button>
            </div>

            <div className="mt-6">
                <h4 className="font-semibold text-gray-800 mb-3">Membres du Bureau</h4>
                {content.members?.map((m: any, idx: number) => (
                    <MemberEditor
                        key={idx}
                        member={m}
                        onChange={(val: any) => updateArrayItem('members', idx, val)}
                        onDelete={() => removeArrayItem('members', idx)}
                    />
                ))}
                <button onClick={() => addArrayItem('members')} className="text-sm text-primary flex items-center gap-1 mt-2">
                    <Plus className="w-4 h-4" /> Ajouter un membre
                </button>
            </div>
        </>
    );
};

const ActivitiesEditor = ({ content, onChange }: any) => {
    // Similar logic for activities (array of objects with title, desc, icon)
    // Simplified strictly for text editing for now, keeping icon fixed or simple text

    const updateActivity = (idx: number, field: string, val: string) => {
        const newItems = [...(content.items || [])];
        newItems[idx] = { ...newItems[idx], [field]: val };
        onChange({ ...content, items: newItems });
    };

    return (
        <>
            <InputField label="Titre" value={content.title} onChange={(v) => onChange({ ...content, title: v })} />

            <div className="mt-4 space-y-4">
                {content.items?.map((item: any, idx: number) => (
                    <div key={idx} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="mb-2">
                            <label className="block text-xs font-medium text-gray-500">Activité {idx + 1}</label>
                            <input
                                value={item.title}
                                onChange={(e) => updateActivity(idx, 'title', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mb-2"
                                placeholder="Titre de l'activité"
                            />
                            <textarea
                                value={item.description}
                                onChange={(e) => updateActivity(idx, 'description', e.target.value)}
                                rows={3}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                                placeholder="Description"
                            />
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};


const VALUES_Icons = ['FaFlask', 'FaHeart', 'FaBookOpen', 'FaUsers', 'FaHandshake', 'FaLightbulb', 'FaUserTie', 'FaGlobeAfrica'];

const ValuesEditor = ({ content, onChange }: any) => {
    const updateValue = (idx: number, field: string, val: string) => {
        const newItems = [...(content.items || [])];
        newItems[idx] = { ...newItems[idx], [field]: val };
        onChange({ ...content, items: newItems });
    };

    return (
        <>
            <InputField label="Titre" value={content.title} onChange={(v) => onChange({ ...content, title: v })} />
            <InputField label="Sous-titre" value={content.subtitle} onChange={(v) => onChange({ ...content, subtitle: v })} />

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {content.items?.map((item: any, idx: number) => (
                    <div key={idx} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <div className="flex gap-2 mb-2">
                            <select
                                value={item.icon}
                                onChange={(e) => updateValue(idx, 'icon', e.target.value)}
                                className="px-2 py-1.5 border border-gray-300 rounded text-sm w-32"
                            >
                                {VALUES_Icons.map(icon => <option key={icon} value={icon}>{icon}</option>)}
                            </select>
                            <input
                                value={item.title}
                                onChange={(e) => updateValue(idx, 'title', e.target.value)}
                                className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                                placeholder="Titre de la valeur"
                            />
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};


// --- Main Page Component ---

const PresentationEditPage: React.FC = () => {
    const [sections, setSections] = useState<PresentationSection[]>([]);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState<string | null>(null);

    // Initial Fetch
    const fetchSections = async () => {
        try {
            const data = await presentationService.getAllContent();
            setSections(data);
        } catch (error) {
            toast.error('Failed to load sections');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchSections(); }, []);

    // Local Update Handling
    const handleLocalUpdate = (key: string, newContent: any) => {
        setSections(prev => prev.map(s => s.section_key === key ? { ...s, content: newContent } : s));
    };

    // Save Handling
    const handleSave = async (key: string) => {
        const section = sections.find(s => s.section_key === key);
        if (!section) return;

        setSavingKey(key);
        try {
            await presentationService.updateSection(key, section.content);
            toast.success('Modifications enregistrées avec succès');
        } catch (error) {
            toast.error("Erreur lors de l'enregistrement");
        } finally {
            setSavingKey(null);
        }
    };

    if (loading) return <LoadingSpinner />;

    // Helper to get section content safely
    const getSection = (key: string) => sections.find(s => s.section_key === key)?.content || {};
    const hasSection = (key: string) => sections.some(s => s.section_key === key);

    return (
        <div className="max-w-5xl mx-auto py-8 px-4">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">Gestion de la page Présentation</h1>
                <p className="text-gray-600 mt-2">Modifiez le contenu de la page publique de présentation via les formulaires ci-dessous.</p>
            </div>

            <div className="space-y-8">
                {hasSection('hero') && (
                    <SectionContainer title="En-tête (Hero)" onSave={() => handleSave('hero')} isSaving={savingKey === 'hero'}>
                        <HeroEditor content={getSection('hero')} onChange={(c: any) => handleLocalUpdate('hero', c)} />
                    </SectionContainer>
                )}

                {hasSection('intro') && (
                    <SectionContainer title="Introduction" onSave={() => handleSave('intro')} isSaving={savingKey === 'intro'}>
                        <IntroEditor content={getSection('intro')} onChange={(c: any) => handleLocalUpdate('intro', c)} />
                    </SectionContainer>
                )}

                {hasSection('values') && (
                    <SectionContainer title="Nos Valeurs" onSave={() => handleSave('values')} isSaving={savingKey === 'values'}>
                        <ValuesEditor content={getSection('values')} onChange={(c: any) => handleLocalUpdate('values', c)} />
                    </SectionContainer>
                )}

                {hasSection('missions') && (
                    <SectionContainer title="Missions" onSave={() => handleSave('missions')} isSaving={savingKey === 'missions'}>
                        <ListEditor content={getSection('missions')} onChange={(c: any) => handleLocalUpdate('missions', c)} itemLabel="Mission" />
                    </SectionContainer>
                )}

                {hasSection('objectives') && (
                    <SectionContainer title="Objectifs" onSave={() => handleSave('objectives')} isSaving={savingKey === 'objectives'}>
                        <ListEditor content={getSection('objectives')} onChange={(c: any) => handleLocalUpdate('objectives', c)} itemLabel="Objectif" />
                    </SectionContainer>
                )}

                {hasSection('activities') && (
                    <SectionContainer title="Activités" onSave={() => handleSave('activities')} isSaving={savingKey === 'activities'}>
                        <ActivitiesEditor content={getSection('activities')} onChange={(c: any) => handleLocalUpdate('activities', c)} />
                    </SectionContainer>
                )}

                {hasSection('bureau') && (
                    <SectionContainer title="Bureau Exécutif" onSave={() => handleSave('bureau')} isSaving={savingKey === 'bureau'}>
                        <BureauEditor content={getSection('bureau')} onChange={(c: any) => handleLocalUpdate('bureau', c)} />
                    </SectionContainer>
                )}
            </div>
        </div>
    );
};

export default PresentationEditPage;
